"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { setFlowData } from "@/components/PageHome/redux/slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  FlowProps,
  ICON_STYLE,
  MODAL_KEYS,
  MODAL_TYPES,
  renderModalContent,
  PREGNANCY_PERIOD_LIMITS,
} from "@/components/PageHome/components/OnboardingSection/Flows/helpers";
import flowSelector from "@/components/PageHome/redux/selectors/flowData";
import { onBoardUser } from "@/components/PageHome/redux/actions";
import { AboutToDeliver as AboutToDeliverAttr } from "@/components/PageHome/redux/types";
import { dateToString } from "@/helpers";
import { APPOINTMENT_URL } from "@/helpers/constants";

const Birth: React.FC<FlowProps> = ({ isModalOpen, setIsModalOpen, dueDate }) => {
  const [selectedModel, setSelectedModel] = React.useState<number>(1);
  const flowData = useAppSelector(flowSelector) as AboutToDeliverAttr;

  const dispatch = useAppDispatch();
  const router = useRouter();

  useEffect(() => {
    const closeFunction = FlowData[selectedModel][MODAL_KEYS.onClose];

    if (isModalOpen === false && closeFunction && typeof closeFunction === "function") {
      closeFunction();
    }
  }, [isModalOpen]);

  const dispatchOnBoarding = (): void => {
    dispatch(onBoardUser());

    if (setIsModalOpen) {
      setIsModalOpen(false);
    }
  };

  const FlowData = {
    1: {
      [MODAL_KEYS.type]: MODAL_TYPES.dueDate,
      [MODAL_KEYS.initialDate]: dueDate,
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            <strong>Woah, you’re almost there!</strong>
          </span>
        ),
      },
      [MODAL_KEYS.description]: {
        prefixText: (
          <span>
            Let us know <strong>when you’re due</strong> so we can share personalised tools and tips about labour
          </span>
        ),
      },
      [MODAL_KEYS.ctaText]: "Submit",
      [MODAL_KEYS.btnClick]: (date: Date): void => {
        if (date) {
          const dateString = dateToString(date);
          dispatch(setFlowData({ ...flowData, dueDate: dateString }));
        }

        dispatchOnBoarding();

        if (setIsModalOpen) {
          setIsModalOpen(false);
        }
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={(): void => {
            setSelectedModel(3);
          }}
        >
          I don’t remember my due date
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style2, className: ["bgIcon"] },
    },
    2: {
      [MODAL_KEYS.type]: MODAL_TYPES.periodDate,
      [MODAL_KEYS.periodLimits]: PREGNANCY_PERIOD_LIMITS,
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            Share your <strong>last period start date</strong> to get details about your pregnancy
          </span>
        ),
      },
      [MODAL_KEYS.description]: "",
      [MODAL_KEYS.ctaBelowText]: (
        <button onClick={dispatchOnBoarding}>I don’t remember when I got my last period</button>
      ),
      [MODAL_KEYS.ctaText]: "Next",
      [MODAL_KEYS.btnClick]: (date: Date): void => {
        if (date) {
          const dateString = dateToString(date);
          dispatch(setFlowData({ ...flowData, periodDate: dateString }));
        }

        setSelectedModel(4);
      },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
    3: {
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            Want to <strong>know your due date?</strong>
          </span>
        ),
      },
      [MODAL_KEYS.description]: {
        prefixText: (
          <span>
            <strong>Book an appointment</strong> with our doctors to set up a sonography and find out your due date
          </span>
        ),
      },
      [MODAL_KEYS.ctaText]: "Book an appointment",
      [MODAL_KEYS.btnClick]: (): void => {
        router.push(APPOINTMENT_URL);
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={(): void => {
            setSelectedModel(2);
          }}
        >
          I don’t want to book an appointment
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style2, className: ["bgIcon"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
    4: {
      [MODAL_KEYS.type]: MODAL_TYPES.periodCycle,
      [MODAL_KEYS.description]: "",
      [MODAL_KEYS.btnClick]: (cycleLength: number): void => {
        if (typeof cycleLength === "number") {
          dispatch(setFlowData({ ...flowData, cycleLength: cycleLength }));
        }

        dispatchOnBoarding();
      },
      [MODAL_KEYS.ctaBelowText]: <button onClick={dispatchOnBoarding}>I don’t remember my average cycle</button>,
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style2, className: ["bgIcon"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
  };

  return renderModalContent(FlowData[selectedModel]);
};

export default Birth;
